import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface StartOverButtonProps {
  onClick?: () => void;
  className?: string;
  skipConfirmation?: boolean;
}

const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const museoStack = '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

const StartOverButton: React.FC<StartOverButtonProps> = ({ onClick, className = '', skipConfirmation = false }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const hasCartItems = useSelector((state: RootState) => state.order.cart.length > 0);

  const confirmationCopy = useMemo(() => {
    const baseCopy = 'Are you sure you want to start over?';
    if (!hasCartItems) {
      return { baseCopy, extraCopy: null } as const;
    }
    return {
      baseCopy,
      extraCopy: 'All items will be removed from your order.\nThis cannot be undone.',
    } as const;
  }, [hasCartItems]);

  const openConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (skipConfirmation) {
      onClick?.();
      return;
    }
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const handleConfirm = () => {
    closeConfirm();
    onClick?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={openConfirm}
        className={`inline-flex w-[200px] h-[60px] items-center justify-center text-center bg-[#ECEAE6] rounded-t-2xl rounded-b-none uppercase tracking-wide ${className}`.trim()}
        style={{
          fontFamily: nexaRustStack,
          fontSize: '20px',
          color: '#B8292F',
          fontWeight: 900,
        }}
      >
        Start Over
      </button>

      {isConfirmOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-8"
          style={{
            backgroundColor: '#f2f1ec',
          }}
        >
          <div
            className="absolute inset-0"
            onClick={closeConfirm}
            aria-hidden="true"
          />

          <div className="relative flex flex-col items-center text-center">
            <h2
              className="font-display font-black uppercase tracking-[2px] text-[#00A294]"
              style={{ fontSize: '60px', lineHeight: '64px' }}
            >
              Start Over
            </h2>

            <p
              className="mt-6 text-[#333333]"
              style={{
                fontFamily: museoStack,
                fontSize: '30px',
                lineHeight: '38px',
              }}
            >
              {confirmationCopy.baseCopy}
            </p>

            {confirmationCopy.extraCopy && (
              <p
                className="mt-4 text-[#333333] whitespace-pre-line"
                style={{
                  fontFamily: museoStack,
                  fontSize: '30px',
                  lineHeight: '38px',
                }}
              >
                {confirmationCopy.extraCopy}
              </p>
            )}

            <div className="mt-12 flex items-center gap-8">
              <button
                type="button"
                onClick={closeConfirm}
                className="px-12 py-4 rounded-[18px] uppercase"
                style={{
                  fontFamily: nexaRustStack,
                  fontSize: '40px',
                  lineHeight: '44px',
                  fontWeight: 900,
                  backgroundColor: 'transparent',
                  color: '#E32B26',
                  border: '2px solid #E32B26',
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirm}
                className="px-12 py-4 rounded-tr-[18px] rounded-bl-[18px] rounded-tl-none rounded-br-none uppercase"
                style={{
                  fontFamily: nexaRustStack,
                  fontSize: '40px',
                  lineHeight: '44px',
                  fontWeight: 900,
                  backgroundColor: '#00A294',
                  color: '#FFFFFF',
                  border: '2px solid #00A294',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StartOverButton;

